import React, { useEffect, useMemo, useState } from "react";
import {
    Alert as MuiAlert,
    Button as MuiButton,
    Dialog as MuiDialog,
    DialogActions as MuiDialogActions,
    DialogContent as MuiDialogContent,
    DialogTitle as MuiDialogTitle,
    FormControl as MuiFormControl,
    InputLabel as MuiInputLabel,
    MenuItem as MuiMenuItem,
    Select as MuiSelect
} from "@mui/material";
import type { IntegrationTargetType, ProviderId } from "@/api/integrations/links";
import type { ProviderState, SourceRow } from "./useIntegrationsData";
import { linkTargetId, linkTargetType } from "./LinkTable";

export interface IntegrationTargetOption {
    type: IntegrationTargetType;
    id: string;
    name: string;
}

export interface ExistingKindLink {
    providerId: ProviderId;
    providerLabel: string;
    externalName: string;
}

interface Props {
    open: boolean;
    state: ProviderState;
    products: IntegrationTargetOption[];
    contextMeshes: IntegrationTargetOption[];
    productKindLinkById: ReadonlyMap<string, ExistingKindLink>;
    linking: boolean;
    initialTarget?: Pick<IntegrationTargetOption, "type" | "id">;
    onConfirm: (row: SourceRow, target: IntegrationTargetOption) => void;
    onCancel: () => void;
}

const sourceLabel = (state: ProviderState) =>
    `${state.meta.name} ${state.meta.kind === "git" ? "Repository" : "Project"}`;

export function LinkTargetDialog({
    open, state, products, contextMeshes, productKindLinkById, linking,
    initialTarget, onConfirm, onCancel
}: Props) {
    const availableTypes = useMemo(() => [
        ...(products.length ? ["product" as const] : []),
        ...(contextMeshes.length ? ["context_mesh" as const] : [])
    ], [contextMeshes.length, products.length]);
    const [targetType, setTargetType] = useState<IntegrationTargetType>("product");
    const [targetId, setTargetId] = useState("");
    const [externalRef, setExternalRef] = useState("");
    const linkableRows = useMemo(() => state.rows.filter(row => row.availableForLinking), [state.rows]);
    const targets = targetType === "product" ? products : contextMeshes;

    useEffect(() => {
        if (!open) return;
        const preferredType = initialTarget && availableTypes.includes(initialTarget.type)
            ? initialTarget.type
            : (availableTypes[0] ?? "product");
        const preferredTargets = preferredType === "product" ? products : contextMeshes;
        setTargetType(preferredType);
        setTargetId(initialTarget?.type === preferredType && preferredTargets.some(item => item.id === initialTarget.id)
            ? initialTarget.id
            : "");
        setExternalRef("");
    }, [availableTypes, contextMeshes, initialTarget, open, products, state.meta.id]);

    const unavailableReason = useMemo(() => {
        const reasons = new Map<string, string>();
        for (const target of targets) {
            if (target.type === "product") {
                const existing = productKindLinkById.get(target.id);
                if (existing) {
                    reasons.set(target.id, `Already linked to ${existing.providerLabel} ${existing.externalName}`);
                    continue;
                }
            }
            const hasAvailableSource = linkableRows.some(row => !row.links.some(link =>
                linkTargetType(link) === target.type && linkTargetId(link) === target.id));
            if (!hasAvailableSource && linkableRows.length > 0) {
                reasons.set(target.id, "Already linked to every available source");
            }
        }
        return reasons;
    }, [linkableRows, productKindLinkById, targets]);

    const sourceOptions = useMemo(() => targetId ? linkableRows.filter(row => !row.links.some(link =>
        linkTargetType(link) === targetType && linkTargetId(link) === targetId)) : [],
    [linkableRows, targetId, targetType]);
    const selectedSource = sourceOptions.find(row => row.externalRef === externalRef);
    const selectedTarget = targets.find(target => target.id === targetId);
    const label = sourceLabel(state);

    return (
        <MuiDialog open={open} onClose={linking ? undefined : onCancel}
            aria-labelledby="link-integration-target-title"
            PaperProps={{ className: "il-root", sx: { width: 480, borderRadius: "14px", p: 1 } }}>
            <MuiDialogTitle id="link-integration-target-title" sx={{ fontSize: 17, fontWeight: 600, pb: 1 }}>
                Link to {state.meta.name}
            </MuiDialogTitle>
            <MuiDialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
                {availableTypes.length > 1 && (
                    <MuiFormControl fullWidth size="small">
                        <MuiInputLabel id="link-target-type-label">Link to</MuiInputLabel>
                        <MuiSelect labelId="link-target-type-label" label="Link to" value={targetType}
                            disabled={linking} onChange={event => {
                                setTargetType(event.target.value as IntegrationTargetType);
                                setTargetId(""); setExternalRef("");
                            }}>
                            <MuiMenuItem value="product">Product</MuiMenuItem>
                            <MuiMenuItem value="context_mesh">Context Mesh</MuiMenuItem>
                        </MuiSelect>
                    </MuiFormControl>
                )}
                <MuiFormControl fullWidth size="small">
                    <MuiInputLabel id="link-target-label">{targetType === "product" ? "Product" : "Context Mesh"}</MuiInputLabel>
                    <MuiSelect labelId="link-target-label" label={targetType === "product" ? "Product" : "Context Mesh"}
                        value={targetId} disabled={linking} onChange={event => { setTargetId(event.target.value); setExternalRef(""); }}>
                        <MuiMenuItem value="" disabled><em>Select a {targetType === "product" ? "product" : "Context Mesh"}</em></MuiMenuItem>
                        {targets.map(target => (
                            <MuiMenuItem key={target.id} value={target.id} disabled={unavailableReason.has(target.id)}>
                                <span>{target.name}</span>
                                {unavailableReason.has(target.id) && <span className="il-select-reason">{unavailableReason.get(target.id)}</span>}
                            </MuiMenuItem>
                        ))}
                    </MuiSelect>
                </MuiFormControl>
                <MuiFormControl fullWidth size="small" disabled={linking || !targetId || sourceOptions.length === 0}>
                    <MuiInputLabel id="link-source-label">{label}</MuiInputLabel>
                    <MuiSelect labelId="link-source-label" label={label} value={externalRef}
                        onChange={event => setExternalRef(event.target.value)}>
                        <MuiMenuItem value="" disabled><em>Select a {label.toLowerCase()}</em></MuiMenuItem>
                        {sourceOptions.map(row => (
                            <MuiMenuItem key={row.externalRef} value={row.externalRef}>
                                {row.keyOrBranch ? `${row.externalName} · ${row.keyOrBranch}` : row.externalName}
                            </MuiMenuItem>
                        ))}
                    </MuiSelect>
                </MuiFormControl>
                {state.listingError && <MuiAlert severity="warning">Available sources could not be refreshed. Try again from Manage.</MuiAlert>}
                {availableTypes.length === 0 && <MuiAlert severity="info">No authorized Products or Context Meshes are available to link.</MuiAlert>}
                {!state.listingError && linkableRows.length === 0 && <MuiAlert severity="info">No {state.meta.name} sources are available.</MuiAlert>}
            </MuiDialogContent>
            <MuiDialogActions sx={{ px: 3, pb: 2, gap: 1.25 }}>
                <MuiButton variant="outlined" disabled={linking} onClick={onCancel}>Cancel</MuiButton>
                <MuiButton variant="contained" disabled={linking || !selectedTarget || !selectedSource}
                    onClick={() => selectedTarget && selectedSource && onConfirm(selectedSource, selectedTarget)}
                    sx={{ bgcolor: "var(--primary)", textTransform: "none" }}>
                    {linking ? "Linking…" : "Link"}
                </MuiButton>
            </MuiDialogActions>
        </MuiDialog>
    );
}
